/**
 * Experiment definition registry.
 *
 * `defineExperiment` declares an AB test with its PostHog flag key and durability tier.
 *
 * The durability tier controls how the assignment is persisted and restored:
 * - `"none"`:  live PostHog evaluation only, no persistence.
 * - `"flow"`:  stored in the Zustand signup-flow store for the current session.
 * - `"user"`:  written to DB table `ab_test_assignment` and restored on AC return so original variant persists.
 */

import { isAbTestVariant } from "@utils/types";

export type Durability = "none" | "flow" | "user";

/** Flag key to variant value (e.g. `{ "shredded-recipes-relaunch": "test" }`). */
export type TExperimentAssignments = Record<string, string>;

export interface ExperimentDefinition {
  /** PostHog feature-flag key. */
  key: string;
  /** How the assignment is persisted (see above). */
  durability: Durability;
  /** Flow form names where the experiment is force-disabled. */
  excludeForms?: ReadonlySet<string>;
}

/** All registered experiments. User-durable experiments are auto-registered. */
const EXPERIMENT_REGISTRY: ExperimentDefinition[] = [];

/**
 * Declares an experiment definition. User-durable experiments (`durability: "user"`)
 * are automatically registered so `getUserDurableAssignments` can read them.
 */
export function defineExperiment(
  config: ExperimentDefinition,
): ExperimentDefinition {
  if (config.durability === "user") {
    EXPERIMENT_REGISTRY.push(config);
  }
  return config;
}

/**
 * Builds a `{ flagKey: variantString }` map for all user-durable experiments
 * by reading their current values via `getFlag`.
 * Experiments whose `excludeForms` contains `formName` are skipped.
 * Returns `undefined` when no assignments are available.
 */
export function getUserDurableAssignments(
  getFlag: (key: string) => string | boolean | undefined,
  formName?: string,
): TExperimentAssignments | undefined {
  const assignments: TExperimentAssignments = {};

  const eligibleExperiments = EXPERIMENT_REGISTRY.filter(
    (experiment) => experiment.durability === "user" && !experiment.excludeForms?.has(formName ?? ""),
  );

  for (const experiment of eligibleExperiments) {
    const value = getFlag(experiment.key);
    if (isAbTestVariant(value)) {
      assignments[experiment.key] = value;
    }
  }

  return Object.keys(assignments).length > 0 ? assignments : undefined;
}

/** @internal Test-only. Clears the global registry between test runs. */
export function resetExperimentRegistry(): void {
  EXPERIMENT_REGISTRY.length = 0;
}
